/**
 * Shared token refresh mutex to prevent race conditions
 * when multiple requests try to refresh the token simultaneously
 */

import { isInvalidRefreshTokenError, clearAuth0Cache } from './authErrors';

// Store Auth0 functions
let getAccessTokenSilently: (() => Promise<string>) | null = null;
let logout: ((options?: { logoutParams?: { returnTo?: string } }) => void) | null = null;

// Shared promise to ensure only one refresh happens at a time
let refreshPromise: Promise<string> | null = null;

export const setAuth0TokenGetter = (getter: () => Promise<string>) => {
  getAccessTokenSilently = getter;
};

export const setAuth0Logout = (logoutFn: (options?: { logoutParams?: { returnTo?: string } }) => void) => {
  logout = logoutFn;
};

export const getAuth0TokenGetter = () => getAccessTokenSilently;

/**
 * Force logout and redirect to login
 * Clears Auth0 cache to ensure a clean state for re-authentication
 */
export const forceLogout = () => {
  clearRefreshPromise();

  // Clear Auth0 cache to prevent stale tokens
  clearAuth0Cache();

  if (logout) {
    // Use Auth0's logout to clear session and redirect
    logout({ logoutParams: { returnTo: window.location.origin } });
  } else {
    // Fallback: just redirect to login
    console.error('Auth0 logout function not available, redirecting to login');
    window.location.href = '/login';
  }
};

/**
 * Get a fresh access token, ensuring only one refresh request happens at a time
 * All concurrent calls will wait for the same refresh to complete
 */
export const getRefreshedToken = async (): Promise<string> => {
  if (!getAccessTokenSilently) {
    throw new Error('Auth0 token getter not available');
  }

  // If a refresh is already in progress, wait for it
  if (refreshPromise) {
    return refreshPromise;
  }

  // Start a new refresh
  refreshPromise = getAccessTokenSilently()
    .catch(error => {
      // Log the actual error to understand its structure
      console.error('Token refresh error details:', {
        error,
        errorType: typeof error,
        errorString: String(error),
        errorJSON: JSON.stringify(error, null, 2),
        errorKeys: error ? Object.keys(error) : [],
      });

      console.error('Refresh token is invalid or expired, forcing logout');
      forceLogout();
      throw new Error('Session expired, please log in again');
    })
    .finally(() => {
      // Clear the promise so future requests can refresh again
      refreshPromise = null;
    });

  return refreshPromise;
};

/**
 * Clear the refresh promise (useful for error handling)
 */
export const clearRefreshPromise = () => {
  refreshPromise = null;
};
